import { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Input } from '@/shared/components/ui/input';
import type { PlannedDate } from '@/shared/types/datetime';
import { minutesBetween } from '@/shared/lib/datetime/planned-date';

// ── Helpers ───────────────────────────────────────────────────────────────────

export function formatMinutes(minutes: number): string {
  if (minutes <= 0) return '';
  const totalDays = minutes / (24 * 60);
  if (Number.isInteger(totalDays) && totalDays >= 1) return `${totalDays}d`;
  if (minutes % 60 === 0) return `${minutes / 60}h`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

// ── Presets ───────────────────────────────────────────────────────────────────

const PRESETS = [
  { label: '30m', minutes: 30 },
  { label: '1h', minutes: 60 },
  { label: '2h', minutes: 120 },
  { label: '4h', minutes: 240 },
  { label: '8h', minutes: 480 },
  { label: '1d', minutes: 1440 },
  { label: '2d', minutes: 2880 },
] as const;

// ── Component ─────────────────────────────────────────────────────────────────

interface EstimatedPickerProps {
  value: number | null;
  onSave: (minutes: number | null) => void;
  disabled?: boolean;
  startDate?: PlannedDate | null;
  dueDate?: PlannedDate | null;
}

export function EstimatedPicker({
  value,
  onSave,
  disabled,
  startDate,
  dueDate,
}: EstimatedPickerProps) {
  const { t } = useTranslation('tasks');
  const [open, setOpen] = useState(false);
  const [localHours, setLocalHours] = useState(0);
  const [localMinutes, setLocalMinutes] = useState(0);

  const currentTotal = localHours * 60 + localMinutes;
  const maxMinutes = startDate && dueDate ? minutesBetween(startDate, dueDate) : null;
  const exceedsPeriod = maxMinutes != null && currentTotal > maxMinutes;

  function handleOpenChange(isOpen: boolean) {
    if (isOpen) {
      const v = value ?? 0;
      setLocalHours(Math.floor(v / 60));
      setLocalMinutes(v % 60);
    } else {
      const capped =
        maxMinutes != null ? Math.min(currentTotal, Math.max(maxMinutes, 0)) : currentTotal;
      const newValue = capped > 0 ? capped : null;
      if (newValue !== value) onSave(newValue);
    }
    setOpen(isOpen);
  }

  function applyPreset(minutes: number) {
    setLocalHours(Math.floor(minutes / 60));
    setLocalMinutes(minutes % 60);
  }

  function handleClear() {
    setLocalHours(0);
    setLocalMinutes(0);
  }

  function handleHoursChange(raw: string) {
    const n = parseInt(raw, 10);
    setLocalHours(isNaN(n) || n < 0 ? 0 : n);
  }

  function handleMinutesChange(raw: string) {
    const n = parseInt(raw, 10);
    setLocalMinutes(isNaN(n) || n < 0 ? 0 : Math.min(n, 59));
  }

  return (
    <div className="group/estimated flex items-center gap-0.5">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild disabled={disabled}>
          <button
            type="button"
            data-testid="tasks-estimated-picker-trigger"
            className="text-xs transition-opacity hover:opacity-70"
          >
            {value ? (
              <span>{formatMinutes(value)}</span>
            ) : (
              <span className="text-muted-foreground">{t('metadata.noEstimated')}</span>
            )}
          </button>
        </PopoverTrigger>

        <PopoverContent
          data-testid="tasks-estimated-picker-popover"
          align="start"
          className="w-60 p-0"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="border-b px-3 py-2.5">
            <span className="text-sm font-semibold">{t('estimatedPicker.title')}</span>
          </div>

          <div className="flex flex-col gap-3 p-3">
            {/* Quick presets */}
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('estimatedPicker.quickAccess')}
              </p>
              <div className="grid grid-cols-4 gap-1.5">
                {PRESETS.map((preset) => {
                  const presetExceedsPeriod = maxMinutes != null && preset.minutes > maxMinutes;
                  return (
                    <button
                      type="button"
                      key={preset.label}
                      disabled={presetExceedsPeriod}
                      onClick={() => applyPreset(preset.minutes)}
                      className={cn(
                        'rounded-md border px-2 py-1.5 text-xs font-medium transition-colors hover:bg-accent',
                        currentTotal === preset.minutes &&
                          'border-primary bg-primary text-primary-foreground hover:bg-primary/90',
                        presetExceedsPeriod && 'cursor-not-allowed opacity-40 hover:bg-transparent',
                      )}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom inputs */}
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('estimatedPicker.custom')}
              </p>
              <div className="flex items-center gap-2">
                <div className="flex flex-1 items-center gap-1">
                  <Input
                    data-testid="tasks-estimated-picker-hours"
                    type="number"
                    min={0}
                    value={localHours === 0 ? '' : String(localHours)}
                    onChange={(e) => handleHoursChange(e.target.value)}
                    placeholder="0"
                    className="h-7 min-w-0 flex-1 text-xs"
                  />
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {t('estimatedPicker.hours')}
                  </span>
                </div>
                <div className="flex flex-1 items-center gap-1">
                  <Input
                    data-testid="tasks-estimated-picker-minutes"
                    type="number"
                    min={0}
                    max={59}
                    value={localMinutes === 0 ? '' : String(localMinutes)}
                    onChange={(e) => handleMinutesChange(e.target.value)}
                    placeholder="0"
                    className="h-7 min-w-0 flex-1 text-xs"
                  />
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {t('estimatedPicker.minutes')}
                  </span>
                </div>
              </div>
              {exceedsPeriod && (
                <p
                  data-testid="tasks-estimated-picker-warning"
                  className="mt-2 text-xs text-warning"
                >
                  {t('estimatedPicker.exceedsPeriod')}
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t px-3 py-2">
            <button
              type="button"
              data-testid="tasks-estimated-picker-clear"
              onClick={handleClear}
              className="flex items-center gap-1 text-xs text-destructive hover:text-destructive/80"
            >
              <Trash2 className="size-3" />
              {t('estimatedPicker.clear')}
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Quick-remove X — visible on hover when there is a value */}
      {value && !disabled && (
        <button
          type="button"
          data-testid="tasks-estimated-picker-remove"
          onClick={() => onSave(null)}
          aria-label={t('estimatedPicker.removeAria')}
          className="rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover/estimated:opacity-100"
        >
          <X className="size-3" />
        </button>
      )}
    </div>
  );
}
